import { memo, useId } from "react";

import { cn } from "@/lib/utils";

export type TextareaFieldProps = {
  label?: string;
  errorMessage?: string;
  maxLength?: number;
} & React.ComponentProps<"textarea">;

const TextareaField = ({
  className,
  id,
  label,
  required,
  value,
  errorMessage,
  maxLength,
  ...props
}: TextareaFieldProps) => {
  const generatedInputId = useId();
  return (
    <div className="grid grid-cols-1 place-content-start gap-2">
      <div className="grid grid-cols-1 place-content-start gap-2 md:gap-2.5">
        {!!label && (
          <label
            htmlFor={id ?? generatedInputId}
            className={cn(
              "text-semantic-text-primary text-base font-normal leading-[130%] md:text-gray-900 md:text-sm md:leading-[20px] md:font-medium",
              {
                "after:content-['*'] after:ml-2 after:text-error": required,
              }
            )}
          >
            {label}
          </label>
        )}
        <div className="relative">
          <textarea
            id={id ?? generatedInputId}
            data-slot="textarea"
            className={cn(
              "outline-none w-full py-[12.8px] px-3 text-base leading-[130%] bg-white text-semantic-text-primary border border-solid border-neutral-200 rounded-[12px] transition-colors disabled:cursor-not-allowed disabled:opacity-50 placeholder:text-muted-foreground placeholder:text-base placeholder:leading-[130%] placeholder:transition-opacity placeholder:opacity-100 placeholder:duration-300 focus-visible:border-blue focus-visible:placeholder:opacity-0 md:py-[19.8px] md:px-7.5 md:rounded-[32px] md:text-sm md:leading-[100%] md:placeholder:text-sm md:placeholder:leading-[100%]",
              {
                "border-error": !!errorMessage,
                "pb-10 md:pb-0": typeof maxLength === "number",
              },
              className
            )}
            required={required}
            maxLength={maxLength}
            value={value}
            {...props}
          />
          {typeof maxLength === "number" && (
            <div className="text-sm leading-[130%] text-grey-700 text-right p-3 absolute bottom-0 right-0 pointer-events-none md:static md:inset-auto md:p-0 md:mt-2.5 md:leading-[20px]">
              <span className="pointer-events-auto">
                {typeof value === "string" ? value.length : 0} / {maxLength}
              </span>
            </div>
          )}
        </div>
      </div>
      {!!errorMessage && (
        <span className="text-error text-sm">{errorMessage}</span>
      )}
    </div>
  );
};

export default memo(TextareaField);

export { TextareaField };
